import axios from 'axios'
import { getExpiryTime, getRecaptcha, getGuestUser, getUserLanguage } from './localStorage'
import { v4 as uuidv4 } from 'uuid'
import { ROUTES } from './constants'

function getOrCreateDeviceId() {
  if (typeof window === 'undefined') return null
  return localStorage.getItem('deviceId')
}

function getCountryCode() {
  const lang = navigator.language || navigator.userLanguage // e.g. "en-US"
  return 'IN'
}

function isTokenValid() {
  return Date.now() < getExpiryTime()
}

let BASE_URL = process.env.REACT_APP_BASE_URL
let AI_BASE_URL = process.env.REACT_APP_AI_TOOLS_BASE_URL
let BASE_URL_CONFIG = process.env.REACT_APP_BASE_URL_CONFIG
let CHATGPT_BASE_URL = process.env.REACT_APP_CHATGPT_BASE_URL
let API_GATEWAY_BASE_URL = process.env.REACT_APP_API_GATEWAY_BASE_URL
let SOCIAL_API_BASE_URL = `${BASE_URL_CONFIG}social/v1`
let SOCIAL_API_DASHBOARD_URL = `${BASE_URL_CONFIG}social/v2`
let CREATOR_API_BASE_URL = `${BASE_URL_CONFIG}creator-economy/v1`
let ASSET_API_BASE_URL = `${BASE_URL_CONFIG}asset/v1`
const TALKING_AVATAR_BASE_URL = `${AI_BASE_URL}`
const TEXT_TO_IMAGE_BASE_URL = AI_BASE_URL
const TEXT_TO_VIDEO_BASE_URL = AI_BASE_URL
const VOICE_REMIX_BASE_URL = AI_BASE_URL

const interceptorData = {
  platform: /Android/i.test(navigator.userAgent)
    ? 'ANDROID'
    : /iPhone|iPad|iPod/i.test(navigator.userAgent)
      ? 'IOS'
      : 'WEB',
  deviceId: getOrCreateDeviceId(),
  countryCode: getCountryCode(),
  isMobile: /Mobi|Android/i.test(navigator.userAgent),
  recaptchaToken: getRecaptcha()
}

let cachedSearch = null
let cachedUrlToken = null

function getUrlAuthToken() {
  if (typeof window === 'undefined') return null
  const currentSearch = window.location.search

  if (currentSearch === cachedSearch) {
    return cachedUrlToken
  }

  const urlParams = new URLSearchParams(currentSearch)
  cachedUrlToken =
    urlParams.get('auth_token') || urlParams.get('externalToken') || null
  cachedSearch = currentSearch

  return cachedUrlToken
}

let isRefreshing = false
let refreshPromise = null
const MAX_REFRESH_RETRIES = 3

const triggerTokenRefresh = async () => {
  if (getGuestUser() === 'true') {
    return Promise.reject("Guest session");
  }
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  const refreshToken = localStorage.getItem('refresh')
  const accessToken = localStorage.getItem('access')

  if (accessToken && !refreshToken) {
    console.warn(
      '[Global Refresh] Access token exists but no refresh token. Logging out...'
    )
    const { performLogoutCleanup, getQueryClient } =
      await import('./logoutUtils')
    const queryClient = getQueryClient()
    await performLogoutCleanup(queryClient)
    throw new Error('No refresh token available')
  }

  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  isRefreshing = true

  refreshPromise = performTokenRefresh(refreshToken).finally(() => {
    isRefreshing = false
    refreshPromise = null
  })

  return refreshPromise
}

const performTokenRefresh = async (refreshToken, retryCount = 0) => {
  try {
    const { encryptPayload } =
      await import('../../../../apps/super-app/src/crypto/encryptPayload.js')
    const { getApiHeaders } = await import('../api/deviceRegistrationApi')

    const headers = getApiHeaders()
    const refreshResponse = await axios.post(
      `${BASE_URL_CONFIG}user/v1/user/refresh_token`,
      { refresh_token: refreshToken },
      {
        headers: {
          ...headers,
          'x-device-id': localStorage.getItem('deviceId')
        },
        timeout: 10000
      }
    )

    if (refreshResponse.data?.data?.auth_token) {
      const newAccessToken = refreshResponse.data.data.auth_token
      localStorage.setItem('access', newAccessToken)

      if (refreshResponse.data.data.refresh_token) {
        localStorage.setItem('refresh', refreshResponse.data.data.refresh_token)
      }

      return newAccessToken
    } else {
      throw new Error('No auth token in refresh response')
    }
  } catch (refreshError) {
    if (retryCount < MAX_REFRESH_RETRIES - 1) {
      const delay = Math.min(1000 * Math.pow(2, retryCount), 5000) // Max 5s delay
      console.warn(
        `[Global Refresh] Token refresh failed (attempt ${retryCount + 1}/${MAX_REFRESH_RETRIES}). Retrying in ${delay}ms...`
      )
      await new Promise((resolve) => setTimeout(resolve, delay))
      return performTokenRefresh(refreshToken, retryCount + 1)
    } else {
      console.error(
        '[Global Refresh] Token refresh failed after maximum retries. Logging out...'
      )

      const { performLogoutCleanup, getQueryClient } =
        await import('./logoutUtils')
      const queryClient = getQueryClient()
      await performLogoutCleanup(queryClient)

      throw refreshError
    }
  }
}

const createRequestInterceptor = (instanceName, config = {}) => {
  const {
    headerStyle = 'x-api-token', // 'x-api-token' | 'Authorization' | 'both'
    includeDeviceId = true,
    includePlatform = false,
    includeCountry = false,
    includeRecaptcha = false,
    customHeaders = {},
    baseUrlSwitcher = null,
    skipAuthEndpoints = [],
    skipTokenForUrls = [], // URLs that should NOT receive auth token
    skipTokenCheck = false
  } = config

  return async (requestConfig) => {
    if (baseUrlSwitcher) {
      requestConfig.baseURL = baseUrlSwitcher(requestConfig.url)
    }

    if (requestConfig.data instanceof FormData) {
      delete requestConfig.headers['Content-Type']
    }

    const isAuthEndpoint = skipAuthEndpoints.some((endpoint) =>
      requestConfig.url?.includes(endpoint)
    )

    // Check if this URL should skip token
    const shouldSkipToken = skipTokenForUrls.some((urlPattern) =>
      requestConfig.url?.includes(urlPattern)
    )

    const accessToken = localStorage.getItem('access')
    const deviceId = localStorage.getItem('deviceId')
    const { countryCode } = interceptorData

    if (!isAuthEndpoint && !shouldSkipToken && accessToken && !skipTokenCheck) {
      if (headerStyle === 'x-api-token' || headerStyle === 'both') {
        requestConfig.headers['x-api-token'] = accessToken
      }
      if (headerStyle === 'Authorization' || headerStyle === 'both') {
        requestConfig.headers['Authorization'] = `Bearer ${accessToken}`
      }
    }

    if (!accessToken && !isAuthEndpoint) {
      const urlToken = getUrlAuthToken()
      if (urlToken) {
        requestConfig.headers['x-api-token'] = urlToken
      }
    }

    if (includeDeviceId && deviceId) {
      requestConfig.headers['x-device-id'] = deviceId
    }

    if (includePlatform) {
      requestConfig.headers['x-platform'] = 'WEB'
    }

    if (
      includeCountry &&
      countryCode &&
      !requestConfig.params?.withoutCountryCode
    ) {
      if (!requestConfig.headers['X-Country'] && !requestConfig.headers['x-country']) {
       requestConfig.headers['x-country'] = countryCode
      }
      if (requestConfig.params && instanceName !== 'creatorEconomyApi') {
        requestConfig.params.country = countryCode
      }
    }

    if (includeRecaptcha) {
      requestConfig.headers['x-recaptcha-token'] = getRecaptcha()
    }

    const isAiProxy =
      requestConfig.url?.includes('aiproxy') ||
      requestConfig.baseURL?.includes('aiproxy')

    if (!isAiProxy) {
      requestConfig.headers['x-language'] = getUserLanguage()
    }

    Object.entries(customHeaders).forEach(([key, value]) => {
      requestConfig.headers[key] = typeof value === 'function' ? value() : value
    })

    return requestConfig
  }
}

const createResponseInterceptor = (
  instanceName,
  axiosInstance,
  config = {}
) => {
  const {
    handle401 = true,
    skipAuthEndpoints = [],
    headerStyle = 'x-api-token'
  } = config

  // return async (error) => {
  //   if (!handle401) {
  //     return Promise.reject(error)
  //   }

  //   const originalRequest = error.config

  //   if (
  //     error.response?.status === 401 &&
  //     !originalRequest._retry &&
  //     !skipAuthEndpoints.some((endpoint) =>
  //       originalRequest.url?.includes(endpoint)
  //     )
  //   ) {
  //     originalRequest._retry = true

  //     try {
  //       const newAccessToken = await triggerTokenRefresh()

  //       if (headerStyle === 'x-api-token' || headerStyle === 'both') {
  //         originalRequest.headers['x-api-token'] = newAccessToken
  //       }
  //       if (headerStyle === 'Authorization' || headerStyle === 'both') {
  //         originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`
  //       }

  //       return axiosInstance(originalRequest)
  //     } catch (refreshError) {
  //       console.error(`[${instanceName}] Token refresh failed:`, refreshError)
  //       return Promise.reject(refreshError)
  //     }
  //   }

  //   return Promise.reject(error)
  // }

  return async (error) => {
    if (!handle401) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    const isGuestUser = getGuestUser() === "true";
    const accessToken = localStorage.getItem("access");

    // Only treat AUTH endpoints as critical
    const authEndpoints = [
      "/refresh_token",
      "/login",
      "/validate_token",
      "/user/profile"
    ];

    const isAuthRequest = authEndpoints.some(endpoint =>
      originalRequest?.url?.includes(endpoint)
    );

    const isAuthServiceDown =
      isAuthRequest &&
      (
        !error.response ||
        error.code === "ECONNABORTED" ||
        error.response?.status >= 500
      );

    // Logout ONLY if:
    // - auth service down
    // - user is logged in
    // - NOT guest user
    if (isAuthServiceDown && !isGuestUser && accessToken) {
      console.error(
        `[${instanceName}] Auth service unavailable. Logging out user...`
      );

      const { performLogoutCleanup, getQueryClient } =
        await import("./logoutUtils");

      const queryClient = getQueryClient();

      await performLogoutCleanup(queryClient);

      window.location.href = "/login";

      return Promise.reject(error);
    }

    // Handle expired token
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !skipAuthEndpoints.some(endpoint =>
        originalRequest.url?.includes(endpoint)
      )
    ) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await triggerTokenRefresh();

        if (headerStyle === "x-api-token" || headerStyle === "both") {
          originalRequest.headers["x-api-token"] = newAccessToken;
        }

        if (headerStyle === "Authorization" || headerStyle === "both") {
          originalRequest.headers["Authorization"] =
            `Bearer ${newAccessToken}`;
        }

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error(
          `[${instanceName}] Token refresh failed. Logging out...`
        );

        if (!isGuestUser && accessToken) {
          const { performLogoutCleanup, getQueryClient } =
            await import("./logoutUtils");

          const queryClient = getQueryClient();

          await performLogoutCleanup(queryClient);

          window.location.href = "/login";
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  };
}

const createAuthInterceptors = (instanceName, axiosInstance, config = {}) => {
  const requestInterceptor = createRequestInterceptor(instanceName, config)
  const responseInterceptor = createResponseInterceptor(
    instanceName,
    axiosInstance,
    config
  )

  axiosInstance.interceptors.request.use(requestInterceptor, (error) =>
    Promise.reject(error)
  )
  axiosInstance.interceptors.response.use(
    (response) => response,
    responseInterceptor
  )
}

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true
})

const actionGenerationUrl = axios.create({
  baseURL: `${AI_BASE_URL}`
})

const faceSwapApi = axios.create({
  baseURL: `${AI_BASE_URL}`
})

const imageToVideoUrl = axios.create({
  baseURL: `${AI_BASE_URL}`
})

const SpiritualAssesmentUrl = axios.create({
  baseURL: `${AI_BASE_URL}${process.env.REACT_APP_ETERNAL_AI_URL}`
})

const textToImageApi = axios.create({
  baseURL: AI_BASE_URL,
  timeout: 60000
})

const textToVideoApi = axios.create({
  baseURL: AI_BASE_URL,
  timeout: 120000
})

const voiceRemixApi = axios.create({
  baseURL: AI_BASE_URL,
  timeout: 120000
})

const talkingAvatarApi = axios.create({
  baseURL: AI_BASE_URL
})

const toolCreditApi = axios.create({
  baseURL: `${BASE_URL}aiproxy/v1`
})

const videoFaceSwapApi = axios.create({
  baseURL: `${AI_BASE_URL}`
})

const backgroundChangeUrl = axios.create({
  baseURL: `${AI_BASE_URL}`
})

const colorizeUrl = axios.create({
  baseURL: `${AI_BASE_URL}`
})

const voiceCloneUrl = axios.create({
  baseURL: `${AI_BASE_URL}`
})

const motionControlUrl = axios.create({
  baseURL: `${AI_BASE_URL}`
})

const ErosAiUrl = axios.create({
  baseURL: `${CHATGPT_BASE_URL}`,
  timeout: 10000
})

const userAppApi = axios.create({
  baseURL: BASE_URL_CONFIG
})

const apiGatewayUrl = axios.create({
  baseURL: API_GATEWAY_BASE_URL
})

const socialApi = axios.create({
  baseURL: SOCIAL_API_BASE_URL
})

const socialDashboardApi = axios.create({
  baseURL: SOCIAL_API_DASHBOARD_URL
})

const creatorEconomyApi = axios.create({
  baseURL: CREATOR_API_BASE_URL
})

const assetApi = axios.create({
  baseURL: ASSET_API_BASE_URL
})

createAuthInterceptors('api', api, {
  headerStyle: 'x-api-token',
  includeDeviceId: true,
  includePlatform: true,
  includeCountry: true,
  includeRecaptcha: true,
  handle401: true,
  skipAuthEndpoints: [ROUTES.login, '/register', '/refresh_token'],
  skipTokenForUrls: ['/settings/', '/v1/payments/plan/get_plans_detailed', '/settings'], // Don't send token for these
  baseUrlSwitcher: (url) => {
    
    const configBaseUrlPaths = [
      'recaptcha/verify',
      'music/api',
      'config/v1/translations/',
      'config/v1/languages/',
      'config/v1/configurations/',
      'search/',
      'template_share/',
      'analytics/v1/event/',
      'legal/documents/active',
    ];
 
    const useConfigBaseUrl = configBaseUrlPaths.some((path) =>
      url?.includes(path)
    );

    return useConfigBaseUrl ? BASE_URL_CONFIG : BASE_URL;
     
  }
})

createAuthInterceptors('userAppApi', userAppApi, {
  headerStyle: 'both', // Both Authorization and x-api-token
  includeDeviceId: true,
  handle401: true,
  // skipAuthEndpoints: ['/refresh_token', '/device/device_registration'],
  skipAuthEndpoints: [
    '/login',
    '/register',
    '/forgot_password',
    '/verify_otp',
    '/resend_otp',
    '/google_login',
    '/refresh_token',
    '/device/device_registration'
  ],
  skipTokenCheck: false // Will validate token but skip for registration
})

createAuthInterceptors('apiGatewayUrl', apiGatewayUrl, {
  headerStyle: 'both',
  includeDeviceId: true,
  includePlatform: true,
  includeCountry: true,
  handle401: true
})

// Social API instance
createAuthInterceptors('socialApi', socialApi, {
  headerStyle: 'x-api-token',
  includeDeviceId: true,
  includePlatform: true,
  includeCountry: true,
  handle401: true,
  customHeaders: {
    accept: 'application/json',
    'x-api-client': 'CZgbPYnmcj5iyEH9tg0GYvB4lm9gGQ9qs6jQwllV',
    'x-version': '1.0.0',
    'x-request-id': () => uuidv4()
  }
})
// Social API Dashboard instance
createAuthInterceptors('socialDashboardApi', socialDashboardApi, {
  headerStyle: 'x-api-token',
  includeDeviceId: true,
  includePlatform: true,
  includeCountry: true,
  handle401: true,
  customHeaders: {
    accept: 'application/json',
    'x-api-client': 'CZgbPYnmcj5iyEH9tg0GYvB4lm9gGQ9qs6jQwllV',
    'x-version': '1.0.0',
    'x-request-id': () => uuidv4()
  }
})
// creator API instance
createAuthInterceptors('creatorEconomyApi', creatorEconomyApi, {
  headerStyle: 'x-api-token',
  includeDeviceId: true,
  includePlatform: true,
  includeCountry: true,
  handle401: true,
  customHeaders: {
    accept: 'application/json',
    'x-api-client': 'CZgbPYnmcj5iyEH9tg0GYvB4lm9gGQ9qs6jQwllV',
    'x-version': '1.0.0',
    'x-request-id': () => uuidv4()
  }
})

// asset API instance
createAuthInterceptors('assetApi', assetApi, {
  headerStyle: 'x-api-token',
  includeDeviceId: true,
  includePlatform: true,
  includeCountry: true,
  handle401: true,
  customHeaders: {
    accept: 'application/json',
    'x-api-client': 'CZgbPYnmcj5iyEH9tg0GYvB4lm9gGQ9qs6jQwllV',
    'x-app-version': '1.0.0',
    'x-request-id': () => uuidv4()
  }
})

// Eros AI URL instance
createAuthInterceptors('ErosAiUrl', ErosAiUrl, {
  headerStyle: 'both',
  includeDeviceId: true,
  includePlatform: true,
  includeCountry: true,
  includeRecaptcha: true,
  handle401: true
})

// AI Tool instances - all use the same minimal config
const aiToolConfig = {
  headerStyle: 'x-api-token',
  includeDeviceId: true,
  handle401: true
}

createAuthInterceptors('faceSwapApi', faceSwapApi, aiToolConfig)
createAuthInterceptors('textToImageApi', textToImageApi, aiToolConfig)
createAuthInterceptors('textToVideoApi', textToVideoApi, aiToolConfig)
createAuthInterceptors('voiceRemixApi', voiceRemixApi, aiToolConfig)
createAuthInterceptors('talkingAvatarApi', talkingAvatarApi, aiToolConfig)
createAuthInterceptors('toolCreditApi', toolCreditApi, aiToolConfig)
createAuthInterceptors('videoFaceSwapApi', videoFaceSwapApi, aiToolConfig)
createAuthInterceptors('backgroundChangeUrl', backgroundChangeUrl, aiToolConfig)
createAuthInterceptors('colorizeUrl', colorizeUrl, aiToolConfig)
createAuthInterceptors('voiceCloneUrl', voiceCloneUrl, aiToolConfig)
createAuthInterceptors('actionGenerationUrl', actionGenerationUrl, aiToolConfig)
createAuthInterceptors('imageToVideoUrl', imageToVideoUrl, aiToolConfig)
createAuthInterceptors('motionControlUrl', motionControlUrl, aiToolConfig)
createAuthInterceptors(
  'SpiritualAssesmentUrl',
  SpiritualAssesmentUrl,
  aiToolConfig
)

export {
  api,
  faceSwapApi,
  videoFaceSwapApi,
  backgroundChangeUrl,
  colorizeUrl,
  ErosAiUrl,
  SpiritualAssesmentUrl,
  TEXT_TO_IMAGE_BASE_URL,
  TEXT_TO_VIDEO_BASE_URL,
  voiceCloneUrl,
  VOICE_REMIX_BASE_URL,
  TALKING_AVATAR_BASE_URL,
  actionGenerationUrl,
  imageToVideoUrl,
  textToImageApi,
  textToVideoApi,
  voiceRemixApi,
  talkingAvatarApi,
  toolCreditApi,
  userAppApi,
  socialApi,
  socialDashboardApi,
  creatorEconomyApi,
  motionControlUrl,
  assetApi,
  apiGatewayUrl
}